import React, { useState, useCallback } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ChevronRight, Folder, FolderOpen, File, Check } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

export interface TreeNode {
  id: string;
  label: string;
  icon?: React.ReactNode;
  children?: TreeNode[];
  disabled?: boolean;
  /** Extra data attached to the node */
  data?: Record<string, unknown>;
}

export type SelectionMode = 'none' | 'single' | 'multiple';

// ─── Variants ─────────────────────────────────────────────────────

const treeViewVariants = cva('w-full', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
    variant: {
      default: '',
      bordered: 'border border-border rounded-lg p-2',
      filled: 'bg-muted rounded-lg p-2',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
});

const treeNodeVariants = cva(
  'flex items-center gap-1.5 rounded-md cursor-pointer transition-colors duration-150 select-none',
  {
    variants: {
      size: {
        sm: 'py-0.5 px-1.5',
        md: 'py-1 px-2',
        lg: 'py-1.5 px-2.5',
      },
      state: {
        default: 'text-foreground hover:bg-muted',
        selected: 'bg-primary-50 text-primary-700 hover:bg-primary-100',
        disabled: 'text-muted-foreground cursor-not-allowed',
      },
    },
    defaultVariants: {
      size: 'md',
      state: 'default',
    },
  }
);

const chevronVariants = cva(
  'shrink-0 text-muted-foreground transition-transform duration-200',
  {
    variants: {
      size: {
        sm: 'w-3 h-3',
        md: 'w-4 h-4',
        lg: 'w-5 h-5',
      },
      expanded: {
        true: 'rotate-90',
        false: 'rotate-0',
      },
    },
    defaultVariants: {
      size: 'md',
      expanded: false,
    },
  }
);

const iconVariants = cva('shrink-0', {
  variants: {
    size: {
      sm: 'w-3.5 h-3.5',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ─── TreeView Props ───────────────────────────────────────────────

export interface TreeViewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>,
    VariantProps<typeof treeViewVariants> {
  nodes: TreeNode[];
  /** Which nodes are expanded — pass for controlled mode */
  expandedIds?: Set<string>;
  /** Called when expanded set changes */
  onExpandChange?: (expandedIds: Set<string>) => void;
  /** Expand all nodes by default (uncontrolled mode) */
  defaultExpandAll?: boolean;
  /** Selection mode */
  selectionMode?: SelectionMode;
  /** Currently selected node IDs */
  selectedIds?: Set<string>;
  /** Called when selection changes */
  onSelect?: (selectedIds: Set<string>, node: TreeNode) => void;
  /** Show default folder/file icons when no icon is provided */
  showDefaultIcons?: boolean;
  /** Custom indentation per level in pixels */
  indentPx?: number;
  /**
   * When true, clicking the row body fires onSelect WITHOUT toggling expand.
   * Only the chevron (clickable area) toggles. Use this when row click should
   * mean "open detail" instead of "expand subtree" — e.g. the Strategic Tree
   * page where clicking a goal opens its edit dialog. Default: false (legacy
   * behaviour — row click does both, kept for backward compatibility).
   */
  chevronOnlyToggle?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────

function collectAllIds(nodes: TreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: TreeNode[]) => {
    for (const node of list) {
      ids.add(node.id);
      if (node.children) walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

// ─── Internal Node Component ──────────────────────────────────────

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  size: 'sm' | 'md' | 'lg';
  selectionMode: SelectionMode;
  expandedIds: Set<string>;
  selectedIds: Set<string>;
  showDefaultIcons: boolean;
  indentPx: number;
  onToggle: (id: string) => void;
  onSelect: (node: TreeNode) => void;
  chevronOnlyToggle: boolean;
}

function TreeNodeRow({
  node,
  depth,
  size,
  selectionMode,
  expandedIds,
  selectedIds,
  showDefaultIcons,
  indentPx,
  onToggle,
  onSelect,
  chevronOnlyToggle,
}: TreeNodeRowProps) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedIds.has(node.id);
  const isDisabled = node.disabled === true;

  const state = isDisabled ? 'disabled' : isSelected ? 'selected' : 'default';

  const handleClick = () => {
    if (isDisabled) return;
    // chevronOnlyToggle: row click means "select" (open detail), NOT toggle.
    // The chevron has its own click handler for toggling. See line further
    // below where the chevron stops event propagation and calls onToggle.
    if (hasChildren && !chevronOnlyToggle) {
      onToggle(node.id);
    }
    if (selectionMode !== 'none') {
      onSelect(node);
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    // Only invoked when chevronOnlyToggle is true (see render below).
    // Stop propagation so the row's onClick doesn't ALSO fire onSelect.
    e.stopPropagation();
    if (hasChildren) {
      onToggle(node.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
    if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
      e.preventDefault();
      onToggle(node.id);
    }
    if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
      e.preventDefault();
      onToggle(node.id);
    }
  };

  // Determine icon
  let iconElement: React.ReactNode = null;
  if (node.icon) {
    iconElement = node.icon;
  } else if (showDefaultIcons) {
    const iconClass = iconVariants({ size });
    if (hasChildren) {
      iconElement = isExpanded ? (
        <FolderOpen className={cn(iconClass, 'text-amber-500')} />
      ) : (
        <Folder className={cn(iconClass, 'text-amber-500')} />
      );
    } else {
      iconElement = <File className={cn(iconClass, 'text-muted-foreground')} />;
    }
  }

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined} aria-selected={isSelected}>
      <div
        className={treeNodeVariants({ size, state })}
        style={{ paddingLeft: `${depth * indentPx + (size === 'sm' ? 6 : 8)}px` }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
      >
        {/* Chevron — only for branch nodes. When chevronOnlyToggle is on,
            the chevron has its own click handler that stops propagation so
            the row's onClick fires onSelect (open detail) without toggling. */}
        {hasChildren ? (
          chevronOnlyToggle ? (
            <button
              type="button"
              onClick={handleChevronClick}
              className="shrink-0 p-0.5 -m-0.5 rounded hover:bg-muted focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              tabIndex={-1}
            >
              <ChevronRight
                className={chevronVariants({ size, expanded: isExpanded })}
                aria-hidden="true"
              />
            </button>
          ) : (
            <ChevronRight
              className={chevronVariants({ size, expanded: isExpanded })}
              aria-hidden="true"
            />
          )
        ) : (
          <span className={cn('shrink-0', size === 'sm' ? 'w-3' : size === 'md' ? 'w-4' : 'w-5')} />
        )}

        {/* Selection checkbox for multi-select */}
        {selectionMode === 'multiple' && (
          <span
            className={cn(
              'shrink-0 flex items-center justify-center rounded border transition-colors duration-150',
              size === 'sm' ? 'w-3.5 h-3.5' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5',
              isSelected
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-border bg-background'
            )}
            aria-hidden="true"
          >
            {isSelected && (
              <Check className={cn(size === 'sm' ? 'w-2.5 h-2.5' : size === 'md' ? 'w-3 h-3' : 'w-3.5 h-3.5')} strokeWidth={3} />
            )}
          </span>
        )}

        {/* Icon */}
        {iconElement}

        {/* Label */}
        <span className="truncate">{node.label}</span>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <ul role="group" className="list-none">
          {node.children!.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              size={size}
              selectionMode={selectionMode}
              expandedIds={expandedIds}
              selectedIds={selectedIds}
              showDefaultIcons={showDefaultIcons}
              indentPx={indentPx}
              onToggle={onToggle}
              onSelect={onSelect}
              chevronOnlyToggle={chevronOnlyToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── TreeView Component ───────────────────────────────────────────

export const TreeView = React.forwardRef<HTMLDivElement, TreeViewProps>(
  (
    {
      nodes,
      size = 'md',
      variant = 'default',
      expandedIds: controlledExpanded,
      onExpandChange,
      defaultExpandAll = false,
      selectionMode = 'none',
      selectedIds: controlledSelected,
      onSelect,
      showDefaultIcons = true,
      indentPx = 16,
      chevronOnlyToggle = false,
      className,
      ...props
    },
    ref
  ) => {
    // Expanded state (controlled or uncontrolled)
    const [internalExpanded, setInternalExpanded] = useState<Set<string>>(
      () => (defaultExpandAll ? collectAllIds(nodes) : new Set())
    );
    const expandedIds = controlledExpanded ?? internalExpanded;

    // Selected state (controlled or uncontrolled)
    const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set());
    const selectedIds = controlledSelected ?? internalSelected;

    const handleToggle = useCallback(
      (id: string) => {
        const next = new Set(expandedIds);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        if (onExpandChange) {
          onExpandChange(next);
        } else {
          setInternalExpanded(next);
        }
      },
      [expandedIds, onExpandChange]
    );

    const handleSelect = useCallback(
      (node: TreeNode) => {
        if (selectionMode === 'none') return;

        let next: Set<string>;
        if (selectionMode === 'single') {
          next = selectedIds.has(node.id) ? new Set() : new Set([node.id]);
        } else {
          next = new Set(selectedIds);
          if (next.has(node.id)) {
            next.delete(node.id);
          } else {
            next.add(node.id);
          }
        }

        if (onSelect) {
          onSelect(next, node);
        } else {
          setInternalSelected(next);
        }
      },
      [selectionMode, selectedIds, onSelect]
    );

    return (
      <div
        ref={ref}
        className={cn(treeViewVariants({ size, variant }), className)}
        {...props}
      >
        <ul role="tree" className="list-none">
          {nodes.map((node) => (
            <TreeNodeRow
              key={node.id}
              node={node}
              depth={0}
              size={size ?? 'md'}
              selectionMode={selectionMode}
              expandedIds={expandedIds}
              selectedIds={selectedIds}
              showDefaultIcons={showDefaultIcons}
              indentPx={indentPx}
              onToggle={handleToggle}
              onSelect={handleSelect}
              chevronOnlyToggle={chevronOnlyToggle}
            />
          ))}
        </ul>
      </div>
    );
  }
);

TreeView.displayName = 'TreeView';
